import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useSession } from '../stores/session';

export const apiClient = axios.create({
  baseURL: '/admin/api/v1',
  timeout: 30_000,
});

apiClient.interceptors.request.use((config) => {
  // Bug B fix: attach the Bearer token from the persisted session. admin-service
  // auth.ts requires `Authorization: Bearer <token>`; without it every protected
  // /admin/api/v1/* call 401s and the response interceptor bounces back to /login.
  const token = useSession.getState().accessToken;
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Auto-inject Idempotency-Key for mutating methods
  const method = config.method?.toUpperCase();
  if (method && ['POST', 'PATCH', 'DELETE'].includes(method)) {
    if (!config.headers['X-Idempotency-Key']) {
      config.headers['X-Idempotency-Key'] = uuidv4();
    }
  }
  return config;
});

// 401 response interceptor: clear session + redirect to /login
apiClient.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err.response?.status === 401) {
      // Use zustand .getState() which is safe to call outside React components
      useSession.getState().clearSession();
      if (typeof window !== 'undefined') {
        // 企微聊天侧边栏（/wecom/*）没有扫码登录页：会话过期就回侧边栏首页重新静默授权
        if (window.location.pathname.startsWith('/wecom/')) {
          window.location.href = '/wecom/sidebar';
        } else if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  },
);

/**
 * Auth client — 扫码登录的公网端点（qrcode / poll / confirm）。
 *
 * 这些路由挂在 /admin/api/auth/wechat-scan（公网，**不带 /v1** —— 扫码时还没
 * token，不能走受保护前缀的鉴权中间件，见 admin-service src/app.ts）。
 * 故意不复用 apiClient：
 *  - baseURL 不带 /v1（否则请求打到 /admin/api/v1/auth/... → 命中受保护前缀 →
 *    authMiddleware 401 → 拿不到二维码）
 *  - 不挂 401 拦截器（登录页本就无 token；poll 万一 401 不该 clearSession 跳
 *    /login —— 人已经在 /login，再跳荒谬）
 *  - 不挂 Idempotency-Key（qrcode 每次生成新 nonce；poll 是 GET；都不需要幂等）
 */
export const authClient = axios.create({
  baseURL: '/admin/api',
  timeout: 30_000,
});
